import { UiNodeInputAttributes } from '@ory/client';
import axios from 'axios';
import { useCallback, useContext, useEffect, useState } from 'react';

import { useAddUserProvenance } from 'hooks/useAddUserProvenance/useAddUserProvenance';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { useReferrer } from 'hooks/useReferrer';
import { getFilteredProvenance } from 'utils/provenance';
import { getConfig } from '../../config';
import { CSRF_TOKEN, PLEASE_TRY_AGAIN } from '../../constants/onboarding';
import { LOGIN_ROUTE } from '../../constants/routes';
import { UserContext } from '../../context/UserContext';
import { createUserSessionFromOrySession } from '../../context/UserContext/utils';
import { ory } from '../../ory.config';
import { useAuth } from '../useAuth/useAuth';
import { useOry } from '../useOry';
import { OryFlowType } from '../useOry/useOry';

/**
 * Represents a state in useLoginFlow
 * @property {boolean} verifyingSession - A boolean value to indicate verifying session.
 * @property {boolean} loading - A boolean value to loading state in any login flow.
 * @property {string} error - Holds error message.
 */
interface ILoginFlowState {
    loading: boolean;
    error: string;
    verifyingSession: boolean;
}

/**
 * Handles ory login flows.
 * More info: https://www.ory.sh/docs/kratos/self-service/flows/user-login
 */
export const useLoginFlow = () => {
    const [state, setState] = useState<ILoginFlowState>({
        loading: false,
        error: '',
        verifyingSession: true
    });
    const { handleError } = useOry();
    const { navigateWithSearch } = useCustomNavigate();
    const { authorizeWithOnboardingFlow, error } = useAuth();
    const { setUserSession } = useContext(UserContext);
    const { captureMantaCareEvent } = useAnalytics();
    const { addUserProvenance } = useAddUserProvenance();
    const { getInitialReferrer } = useReferrer();

    useEffect(() => {
        setState(state => ({ ...state, error }));
    }, [error]);

    /**
     * Set error in the state.
     */
    const setFlowError = useCallback(
        (error: string) => {
            setState(state => ({ ...state, error, verifyingSession: false, loading: false }));
        },
        [setState]
    );

    /**
     * Verify if a session already exists.
     * If session exists navigate user to home page
     */
    const verifySession = useCallback(async () => {
        try {
            setState(state => ({ ...state, verifyingSession: true }));
            await authorizeWithOnboardingFlow();
            setState(state => ({ ...state, verifyingSession: false }));
        } catch (e) {
            setState(state => ({ ...state, verifyingSession: false }));
        }
    }, [setState]);

    /**
     * Clears error in the state
     */
    const clearError = useCallback(() => {
        setState(state => ({ ...state, error: '', verifyingSession: false, loading: false }));
    }, [setState]);

    /**
     * A common function to handle error in ory login flow.
     */
    const handleOryError = useCallback(
        (e: unknown) => {
            setState(state => ({ ...state, loading: false }));
            if (axios.isAxiosError(e)) {
                handleError(OryFlowType.LOGIN, undefined, setFlowError)(e);
            } else {
                setFlowError(PLEASE_TRY_AGAIN);
            }
        },
        [setFlowError, handleError]
    );

    /**
     * Handles the ORY update login flow.
     * If the flow state or CSRF token is missing, it will redirect to the login page.
     * Otherwise, it calls the ORY update login flow with login credentials to perform the login.
     * On successful login keep the user session in the user context then navigate to dashboard.
     */
    const login = useCallback(
        async (email: string, password: string) => {
            try {
                setState(state => ({ ...state, loading: true, error: '' }));
                const { data: flow } = await ory.createBrowserLoginFlow({});
                const csrfNode = flow.ui.nodes.find(n => {
                    return (n.attributes as UiNodeInputAttributes).name === CSRF_TOKEN;
                });
                if (!csrfNode) {
                    navigateWithSearch({ pathname: LOGIN_ROUTE });
                    return;
                }
                await ory.updateLoginFlow({
                    flow: flow.id,
                    updateLoginFlowBody: {
                        password,
                        csrf_token: (csrfNode.attributes as UiNodeInputAttributes).value,
                        method: 'password',
                        identifier: email.toLowerCase()
                    }
                });
                const { data: session } = await ory.toSession({ tokenizeAs: getConfig().ORY_JWT_TOKENIZE_AS });
                const userSession = createUserSessionFromOrySession(session);
                setUserSession(userSession);
                //After login session is immediately available, even if we set the session in context it is not available
                //to authorizeWithOnboardingFlow because of the react default behavior.
                //Passing session to it directly to login flow.
                const user = (await authorizeWithOnboardingFlow(userSession)) ?? undefined;
                if (getFilteredProvenance()) {
                    addUserProvenance(user?.id, {
                        ...getFilteredProvenance(),
                        referrer: getInitialReferrer(),
                        path: window.location.origin + '/login' + window.location.search
                    });
                }
                captureMantaCareEvent({ type: 'session', action: 'login' }, user);
                setState(state => ({ ...state, loading: false, error: '' }));
            } catch (e) {
                handleOryError(e);
            }
        },
        [handleOryError, navigateWithSearch, setState, setUserSession, captureMantaCareEvent]
    );

    return {
        clearError: clearError,
        error: state.error,
        loading: state.loading,
        verifySession,
        verifyingSession: state.verifyingSession,
        login
    };
};
